/**
 * Utilitários para gerenciamento de rotas protegidas
 * 
 * Este arquivo centraliza a lógica de rotas protegidas para evitar duplicação
 * e garantir que novas rotas sejam automaticamente reconhecidas como protegidas.
 */

/**
 * Rotas que requerem autenticação
 * Estas rotas são definidas dentro de <LoggedArea /> no Routes.tsx
 * 
 * IMPORTANTE: Ao adicionar novas rotas protegidas no Routes.tsx,
 * elas devem começar com um dos prefixos abaixo para serem
 * automaticamente reconhecidas como protegidas.
 */
export const PROTECTED_ROUTE_PREFIXES = [
  '/job-subscriptions',
  '/offers/panel',
  '/offers/create',
  '/offers/', // Prefixo para rotas como /offers/:offer_id, /offers/:offer_id/candidates, etc
  '/profile', // Prefixo para /profile/candidate, /profile/company/user, /profile/job-manager, etc
] as const;

/**
 * Rotas específicas que são protegidas (exatas ou com parâmetros)
 * Use apenas para rotas que não seguem os prefixos acima
 */
export const PROTECTED_ROUTES = [
  '/job-subscriptions',
  '/offers/panel',
  '/offers/create',
  '/profile/candidate',
  '/profile/company/user',
  '/profile/job-manager',
] as const;

/**
 * Rotas administrativas de ofertas que NÃO são rotas de detalhes
 * (usado para excluir da detecção de rotas de detalhes)
 */
const ADMIN_OFFER_ROUTES = [
  '/offers/panel',
  '/offers/create'
] as const;

/**
 * Verifica se uma rota é protegida (requer autenticação)
 * 
 * @param pathname - Caminho da rota a ser verificada
 * @returns true se a rota for protegida, false caso contrário
 * 
 * @example
 * ```typescript
 * isProtectedRoute('/profile/candidate') // true
 * isProtectedRoute('/offers/123') // true
 * isProtectedRoute('/offers') // false (rota pública)
 * isProtectedRoute('/login') // false
 * ```
 */
export const isProtectedRoute = (pathname: string): boolean => {
  // Verificar se a rota corresponde exatamente a uma rota protegida
  if (PROTECTED_ROUTES.some(route => pathname === route || pathname.startsWith(route + '/'))) {
    return true;
  }
  
  // Verificar se a rota começa com algum dos prefixos protegidos
  return PROTECTED_ROUTE_PREFIXES.some(prefix => {
    // Para prefixos que terminam com '/', verificar se a rota começa com o prefixo
    // e não é exatamente o prefixo sem a barra final (ex: /offers não deve ser protegido)
    if (prefix.endsWith('/')) {
      const prefixWithoutSlash = prefix.slice(0, -1);
      // Se for exatamente o prefixo sem a barra, não é protegido
      if (pathname === prefixWithoutSlash) {
        return false;
      }
      return pathname.startsWith(prefix);
    }
    // Para prefixos exatos, verificar correspondência exata ou se começa com o prefixo + '/'
    return pathname === prefix || pathname.startsWith(prefix + '/');
  });
};

/**
 * Verifica se uma rota é uma rota de detalhes de oferta
 * (requer tratamento especial para redirecionamento após login)
 * 
 * Rotas de detalhes são aquelas que mostram uma oferta específica ou candidatos de uma oferta,
 * mas NÃO incluem rotas administrativas como /offers/panel ou /offers/create
 * 
 * @param pathname - Caminho da rota a ser verificada
 * @returns true se for uma rota de detalhes de oferta
 * 
 * @example
 * ```typescript
 * isJobDetailRoute('/offers/123') // true - detalhe de oferta
 * isJobDetailRoute('/offers/123/candidates') // true - candidatos de oferta
 * isJobDetailRoute('/job-subscriptions') // true - inscrições do candidato
 * isJobDetailRoute('/offers/panel') // false - painel administrativo
 * isJobDetailRoute('/offers/create') // false - criar oferta
 * ```
 */
export const isJobDetailRoute = (pathname: string): boolean => {
  // Excluir rotas administrativas
  if (ADMIN_OFFER_ROUTES.some(route => pathname === route || pathname.startsWith(route + '/'))) {
    return false;
  }
  
  // Verificar se é /job-subscriptions (lista de inscrições do candidato)
  if (pathname === '/job-subscriptions' || pathname.startsWith('/job-subscriptions/')) {
    return true;
  }
  
  // Verificar se é uma rota de detalhes de oferta (começa com /offers/ e tem um ID)
  // Padrão: /offers/:offer_id ou /offers/:offer_id/...
  // Exclui /offers (sem barra) e /offers/ (apenas barra)
  if (pathname.startsWith('/offers/')) {
    // Verificar se tem pelo menos um segmento após /offers/
    // Ex: /offers/123, /offers/123/candidates, etc
    const afterOffers = pathname.substring('/offers/'.length);
    // Se tiver conteúdo e não for 'panel' ou 'create', é uma rota de detalhes
    if (afterOffers && !afterOffers.startsWith('panel') && !afterOffers.startsWith('create')) {
      return true;
    }
  }
  
  return false;
};

